import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useClientTiers } from '../auth/ClientTiersProvider';
import { useBrand } from '../theme/BrandProvider';
import { AccountSwitcher } from '../components/AccountSwitcher';
import { OwnAccountOnlyNotice } from '../components/OwnAccountOnlyNotice';
import { satisfiesTiers } from '../auth/TierGuard';

/**
 * One entry in the flat client nav. `requiredTier` hides the item unless the caller holds that
 * tier; `anyOfTiers` (#20070) hides it unless the caller holds ANY of the listed tiers. The filter
 * is presentation — TierGuard on the route and the controller's authorisation are the enforcement.
 */
export interface ClientNavItem {
  path: string;
  label: string;
  icon: string;
  requiredTier?: string;
  anyOfTiers?: readonly string[];
}

const navItems: ClientNavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/cases', label: 'My Tickets', icon: '🎫' },
  { path: '/invoices', label: 'Invoices', icon: '💰' },
  { path: '/it-admin/quotes', label: 'Quotes', icon: '📄', requiredTier: 'ClientAdmin' },
  { path: '/it-admin/departments', label: 'Departments', icon: '🏢', requiredTier: 'ClientAdmin' },
  { path: '/m365-users', label: 'Users', icon: '👥' },
  { path: '/licences', label: 'Licences', icon: '🔑' },
  // #16316 — no requiredTier: browsing the catalogue is informational and open to all three
  // client tiers. The ClientAdmin gate belongs to the basket/checkout actions in S2 (#16317).
  { path: '/shop', label: 'Shop', icon: '🛒' },
  // #16317 — the cart IS the active draft basket. requiredTier, unlike Shop above: browsing is
  // informational and open to every client tier, but the cart and "Send for pricing" are a
  // commercial act and the API is ClientAdmin-only. The filter here is presentation; TierGuard on
  // the route and the controller's authorisation are the enforcement.
  { path: '/cart', label: 'Cart', icon: '🧺', requiredTier: 'ClientAdmin' },
  { path: '/domains', label: 'Domains & DNS', icon: '🌐' },
  { path: '/security-posture', label: 'Security', icon: '🛡️' },
  // #20070 — F11.5 self-service (spec §9.2). Two items in this FLAT list: the prototype's "Tenant"
  // heading is a prototype grouping, not a nav feature this release adds. My requests is
  // ClientAdmin OR ClientUser (ClientAdminAndUser) and never ClientBilling; Approvals is
  // ClientAdmin only (ClientAdminOnly). Each renders an In-Development page until its screen lands.
  { path: '/self-service/requests', label: 'My requests', icon: '📨', anyOfTiers: ['ClientAdmin', 'ClientUser'] },
  { path: '/self-service/approvals', label: 'Approvals', icon: '✅', requiredTier: 'ClientAdmin' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

export function ClientLayout() {
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const { displayName, email, logout } = useAuth();
  const { tiers, hasTier } = useClientTiers();
  const { assets } = useBrand();
  const visibleNavItems = navItems.filter((item) => satisfiesTiers(hasTier, item.requiredTier, item.anyOfTiers));

  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : email?.slice(0, 2).toUpperCase() ?? '??';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-sand-200">
        <div className="px-4 pt-4 pb-3 border-b border-sand-200">
          <Link to="/" aria-label={assets.homeAriaLabel} className="block">
            <img
              src={assets.logo}
              alt={assets.logoAlt}
              className="h-8 w-auto"
              width={183}
              height={59}
            />
          </Link>
          <p className="text-[10px] uppercase tracking-[0.2em] text-sage-500 mt-2">Client Portal</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
          {visibleNavItems.map(item => (
            <NavLink key={item.path} to={item.path}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${isActive ? 'bg-sand-100 text-fern-600' : 'text-gray-700 hover:bg-sand-50 hover:text-fern-600'}`}>
              <span>{item.icon}</span><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="h-16 flex items-center justify-between border-b border-sand-200 bg-white px-4">
          <Link to="/" aria-label={assets.homeAriaLabel} className="md:hidden">
            <img
              src={assets.logo}
              alt={assets.logoAlt}
              className="h-7 w-auto"
              width={183}
              height={59}
            />
          </Link>
          {/* #16306 — account context switcher: beside the brand, not buried in the
              profile menu, so the active client stays visible while working (AC-7). */}
          <AccountSwitcher />
          <div className="flex-1" />
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 rounded-md p-1 hover:bg-sand-50"
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full bg-fern-600 flex items-center justify-center text-white text-xs font-medium">
                {initials}
              </div>
              <span className="hidden lg:block text-sm font-medium text-gray-800">{displayName}</span>
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-64 rounded-lg border border-sand-200 bg-white shadow-lg z-50">
                <div className="px-4 py-3 border-b border-sand-200">
                  <p className="text-sm font-medium text-gray-900">{displayName}</p>
                  <p className="text-xs text-sage-500 truncate">{email}</p>
                  {tiers.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {tiers.map(t => (
                        <span key={t} className="inline-flex items-center rounded bg-sand-100 px-1.5 py-0.5 text-[10px] font-medium text-fern-600">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setProfileOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-5xl">
            <OwnAccountOnlyNotice />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
